import { useEffect, useRef, useState } from "react";
import { useConvex } from "convex/react";
import type { CatalogueInventoryPage } from "@reminder-tracker/backend-contracts";
import { getCatalogueInventory } from "../../backend/functions";
import { classifyCatalogueFailure } from "./failure";
import { formatAdministrationTime } from "../../ui/administration-time";
import {
  AdministrationAuditDetails,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

/** Operate extension: ruled coverage table, truthful partial counts, no customer activity. */
export function CatalogueInventory({ onClose }: { onClose?: () => void }): React.JSX.Element {
  const client = useConvex();
  const [page, setPage] = useState<CatalogueInventoryPage | null>(null);
  const [rows, setRows] = useState<CatalogueInventoryPage["items"]>([]);
  const [busy, setBusy] = useState(false);
  const [breakdown, setBreakdown] = useState<"marketCode" | "latestState">("marketCode");
  const [error, setError] = useState<string | null>(null);
  const [securityFailure, setSecurityFailure] = useState<Error | null>(null);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function load(more: boolean) {
    if (pending.current || (more && page?.nextCursor == null)) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        client.query(getCatalogueInventory, {
          cursor: more ? page!.nextCursor : null,
          pageSize: 50,
          correlationId: `corr_${crypto.randomUUID().replaceAll("-", "")}`,
        }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error("Inventory timed out")), 15_000);
        }),
      ]);
      if (!mounted.current) return;
      // Deduplicate by definition if the catalogue changed between bounded reads.
      const merged = new Map((more ? rows : []).map((row) => [row.presetId, row]));
      for (const row of result.items) merged.set(row.presetId, row);
      setRows([...merged.values()]);
      setPage(result);
    } catch (cause) {
      if (!mounted.current) return;
      setRows([]);
      setPage(null);
      if (classifyCatalogueFailure(cause).kind === "security")
        setSecurityFailure(
          Object.assign(new Error("Verify your administrator access before continuing."), {
            data: { code: "REAUTHENTICATION_REQUIRED" },
          }),
        );
      else setError("We couldn't load catalogue coverage. Refresh to try again.");
    } finally {
      clearTimeout(timer);
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  if (securityFailure !== null) throw securityFailure;
  const today =
    page === null ? "" : new Date(page.observedAtEpochMilliseconds).toISOString().slice(0, 10);
  const groups =
    breakdown === "marketCode"
      ? ["GB", "US", "AE", "SA", "IN", "SG", "AU"]
      : ["draft", "reviewed", "published", "withdrawn"];
  return (
    <div className="catalogue-inventory">
      {onClose ? (
        <button type="button" onClick={onClose}>
          Back to catalogue
        </button>
      ) : null}
      <AdministrationWorkSurface
        id="catalogue-coverage-title"
        title="Catalogue coverage"
        purpose="Review editorial coverage by market or publication state without mixing it with customer usage."
        state={{
          label:
            page === null
              ? "Not loaded"
              : page.nextCursor === null
                ? "Complete loaded view"
                : "More definitions available",
          tone: page === null || page.nextCursor !== null ? "monitor" : "neutral",
        }}
        consequence="Refreshing reads current definitions only. A newer draft never replaces a published version until the separate publication decision is completed."
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void load(false);
          }}
        >
          Refresh coverage
        </button>
        {busy ? <p role="status">Loading catalogue coverage…</p> : null}
        {error !== null ? <p role="alert">{error}</p> : null}
        {page !== null ? (
          <>
            <p role="status">
              {page.nextCursor === null
                ? `All ${rows.length} definitions loaded.`
                : `${rows.length} definitions loaded — more pages remain.`}
            </p>
            <p>
              {rows.filter((row) => row.publishedVersion !== null).length} have a version in its
              publication window.
            </p>
            <p>
              {rows.filter((row) => row.nextReviewOn <= today).length} latest versions are due for
              content review.
            </p>
            <AdministrationAuditDetails summary="Coverage completeness and observation time">
              <p>
                Counts cover the loaded definitions only. Pages are read separately; refresh after
                catalogue changes. Publication windows do not certify content freshness.
              </p>
              <p>Latest page read: {formatAdministrationTime(page.observedAtEpochMilliseconds)}.</p>
            </AdministrationAuditDetails>
            <label>
              Coverage breakdown
              <select
                value={breakdown}
                onChange={(event) =>
                  setBreakdown(event.target.value as "marketCode" | "latestState")
                }
              >
                <option value="marketCode">Catalogue market</option>
                <option value="latestState">Latest editorial status</option>
              </select>
            </label>
            <table className="timeline">
              <caption>Counts for loaded definitions</caption>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Definitions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group}>
                    <td data-label="Group">{group}</td>
                    <td data-label="Definitions">
                      {rows.filter((row) => row[breakdown] === group).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="timeline">
              <caption>Loaded catalogue definitions</caption>
              <thead>
                <tr>
                  <th>Preset</th>
                  <th>Market</th>
                  <th>Latest editorial version</th>
                  <th>Publication window</th>
                  <th>Next content review</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.presetId}>
                    <td data-label="Preset">{row.title}</td>
                    <td data-label="Market">{row.marketCode}</td>
                    <td data-label="Latest editorial version">
                      Version {row.latestVersion} · {row.latestState}
                    </td>
                    <td data-label="Publication window">
                      {row.publishedVersion === null
                        ? "No current version"
                        : `Version ${row.publishedVersion}`}
                    </td>
                    <td data-label="Next content review">
                      {new Intl.DateTimeFormat("en-GB", {
                        dateStyle: "medium",
                        timeZone: "UTC",
                      }).format(new Date(`${row.nextReviewOn}T00:00:00Z`))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {page.nextCursor !== null ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  void load(true);
                }}
              >
                Load more definitions
              </button>
            ) : null}
          </>
        ) : !busy && error === null ? (
          <p>Refresh coverage to read the current catalogue.</p>
        ) : null}
      </AdministrationWorkSurface>
    </div>
  );
}
